<template>
    <section class="flex flex-col gap-4 px-5 pt-4 pb-5">
        <div class="flex items-center gap-2 flex-wrap">
            <div v-if="showRaters" class="flex items-center gap-2" role="group" :aria-label="t('reflectionsAdmin.panel.groupLabel')">
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="rounded-full scaffold-body-small"
                    :class="{ 'bg-toggle-option-selected': tab === 'participants' }"
                    :aria-pressed="tab === 'participants'"
                    @click="switchTab('participants')">
                    {{ t("reflectionsAdmin.panel.participantsTab", { count: participantCount }) }}
                </BaseButton>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="rounded-full scaffold-body-small"
                    :class="{ 'bg-toggle-option-selected': tab === 'raters' }"
                    :aria-pressed="tab === 'raters'"
                    @click="switchTab('raters')">
                    {{ t("reflectionsAdmin.panel.ratersTab", { count: ratersInvited }) }}
                </BaseButton>
            </div>
            <h3 v-else class="body-semibold text-ink">{{ t("reflectionsAdmin.panel.participantsHeading", { count: participantCount }) }}</h3>
            <div class="ml-auto flex items-center gap-2 flex-wrap">
                <BaseButton
                    v-for="option in filterOptions"
                    :key="option.value"
                    theme="neutral"
                    size="sm"
                    class="rounded-full scaffold-body-small"
                    :class="{ 'bg-toggle-option-selected': filter === option.value }"
                    :aria-pressed="filter === option.value"
                    @click="selectFilter(option.value)">
                    {{ option.label }}
                </BaseButton>
                <div class="w-full md:w-auto md:min-w-80 md:max-w-md">
                    <BaseInput v-model="query" type="search" :placeholder="searchLabel" :aria-label="searchLabel">
                        <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                    </BaseInput>
                </div>
            </div>
        </div>

        <p v-if="loadError" class="body-small text-status-error" role="alert">{{ loadError }}</p>
        <p v-else-if="loading && !page" class="body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</p>
        <p v-else-if="page && page.total === 0" class="body-small text-ink-muted">{{ emptyText }}</p>

        <template v-else-if="page">
            <AdminParticipantTable
                v-if="tab === 'participants'"
                :rows="(page as AdminParticipantsPage).rows"
                :hide-rater-status="hideRaterStatus"
                :show-raters="!isSelfAssessmentOnly"
                :remindable="canRemindParticipants"
                :class="{ 'opacity-60': loading }"
                @remind="openParticipantReminder" />
            <AdminRaterTable
                v-else
                :rows="(page as AdminRatersPage).rows"
                :campaign-closed="campaignState === 'closed'"
                :remindable="canRemindRaters"
                :class="{ 'opacity-60': loading }"
                @remind="openRaterReminder" />

            <div class="flex items-center gap-3 flex-wrap px-2 details text-ink-muted">
                <span>{{ footerText }}</span>
                <BaseButton v-if="showRemindAll" theme="neutral" size="sm" :disabled="pendingCount === 0 || sending" @click="openBulkReminder">
                    <i class="bi bi-send" aria-hidden="true"></i>
                    {{ t("reflectionsAdmin.panel.remind.button") }}
                </BaseButton>
                <div class="ml-auto inline-flex items-center gap-1 max-md:basis-full max-md:justify-end">
                    <BaseButton
                        theme="neutral"
                        size="sm"
                        icon-only
                        :aria-label="t('reflectionsAdmin.panel.previousPage')"
                        :disabled="page.page <= 1 || loading"
                        @click="goToPage(page.page - 1)">
                        <i class="bi bi-chevron-left" aria-hidden="true"></i>
                    </BaseButton>
                    <span class="min-w-18 text-center">{{ t("reflectionsAdmin.panel.pageOf", { page: page.page, pages: page.page_count }) }}</span>
                    <BaseButton
                        theme="neutral"
                        size="sm"
                        icon-only
                        :aria-label="t('reflectionsAdmin.panel.nextPage')"
                        :disabled="page.page >= page.page_count || loading"
                        @click="goToPage(page.page + 1)">
                        <i class="bi bi-chevron-right" aria-hidden="true"></i>
                    </BaseButton>
                </div>
            </div>
        </template>

        <ConfirmationDialog
            v-model:open="reminderOpen"
            :title="t('reflectionsAdmin.panel.remind.title')"
            :confirm-label="t('reflectionsAdmin.panel.remind.confirm')"
            :confirm-disabled="sending"
            :cancel-disabled="sending"
            :close-on-confirm="false"
            @confirm="sendReminder">
            <template #content>
                <div v-if="reminderTarget" class="flex flex-col gap-3 body text-ink-muted">
                    <p class="text-ink">{{ reminderSummary }}</p>
                    <p>
                        {{
                            reminderTarget.audience === "participants"
                                ? t("reflectionsAdmin.panel.remind.participantsExplainer")
                                : t("reflectionsAdmin.panel.remind.ratersExplainer")
                        }}
                    </p>
                    <p>{{ t("reflectionsAdmin.panel.remind.cooldownNote") }}</p>
                </div>
            </template>
        </ConfirmationDialog>

        <BaseToast v-if="toast" :key="toast.id" :theme="toast.theme" :description="toast.text" :truncate-length="200" />
    </section>
</template>

<script setup lang="ts">
import { refDebounced } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type {
    AdminParticipantFilter,
    AdminParticipantRow,
    AdminParticipantsPage,
    AdminRaterFilter,
    AdminRaterRow,
    AdminRatersPage,
    AdminReminderResult,
    ReflectionCampaignState,
} from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import AdminParticipantTable from "~vue/pages/reflections/AdminParticipantTable.vue";
import AdminRaterTable from "~vue/pages/reflections/AdminRaterTable.vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

type PanelTab = "participants" | "raters";
type PanelFilter = AdminParticipantFilter | AdminRaterFilter;

// Who a reminder goes to: everyone still outstanding on the tab, or one row of it.
type ReminderTarget = { audience: "participants"; userUuid?: string; name?: string } | { audience: "raters"; inviteId?: string; name?: string };

interface Props {
    campaignId: string;
    campaignName: string;
    campaignState: ReflectionCampaignState;
    participantCount: number;
    submittedCount: number;
    ratersInvited: number;
    // Null when the org anonymizes rater status.
    ratersResponded?: number | null;
    hideRaterStatus?: boolean;
    isSelfAssessmentOnly?: boolean;
    selfAssessEnabled?: boolean;
}

const SEARCH_DEBOUNCE_MS = 300;

const {
    campaignId,
    campaignName,
    campaignState,
    participantCount,
    submittedCount,
    ratersInvited,
    ratersResponded = null,
    hideRaterStatus = false,
    isSelfAssessmentOnly = false,
    selfAssessEnabled = true,
} = defineProps<Props>();
const { t } = useI18n();

const tab = ref<PanelTab>("participants");
const filter = ref<PanelFilter>("all");
const query = ref("");
const debouncedQuery = refDebounced(query, SEARCH_DEBOUNCE_MS);
// What requests actually use. Typing reaches it through the debounce; a tab
// switch clears it at once so the new tab's first fetch never carries the old term.
const effectiveQuery = ref("");
const pageNumber = ref(1);
const page = ref<AdminParticipantsPage | AdminRatersPage | null>(null);
const loading = ref(false);
const loadError = ref<string | null>(null);

const showRaters = computed(() => !hideRaterStatus && !isSelfAssessmentOnly);
const onParticipants = computed(() => tab.value === "participants");

// Reminders are an active-campaign action: a closed campaign has nothing left to
// chase. Participants are only reminded about a self reflection the campaign asks
// for, and raters only where the org lets admins see who has responded.
const canRemindParticipants = computed(() => campaignState === "active" && selfAssessEnabled);
const canRemindRaters = computed(() => campaignState === "active" && showRaters.value);
const showRemindAll = computed(() => (onParticipants.value ? canRemindParticipants.value : canRemindRaters.value));
const pendingCount = computed(() => (onParticipants.value ? Math.max(0, participantCount - submittedCount) : Math.max(0, ratersInvited - (ratersResponded ?? 0))));

const reminderOpen = ref(false);
const reminderTarget = ref<ReminderTarget | null>(null);
const sending = ref(false);
type ToastMessage = { theme: "success" | "info" | "error"; text: string };
const toast = ref<(ToastMessage & { id: number }) | null>(null);
let toastSequence = 0;

const reminderSummary = computed(() => {
    const target = reminderTarget.value;
    if (!target) {
        return "";
    }
    if (target.audience === "participants") {
        return target.userUuid
            ? t("reflectionsAdmin.panel.remind.oneParticipant", { name: target.name, campaign: campaignName })
            : t("reflectionsAdmin.panel.remind.allParticipants", { count: pendingCount.value, campaign: campaignName });
    }
    return target.inviteId
        ? t("reflectionsAdmin.panel.remind.oneRater", { name: target.name, campaign: campaignName })
        : t("reflectionsAdmin.panel.remind.allRaters", { count: pendingCount.value, campaign: campaignName });
});

const filterOptions = computed<{ value: PanelFilter; label: string }[]>(() => {
    if (onParticipants.value) {
        return [
            { value: "all", label: t("reflectionsAdmin.panel.filter.all") },
            { value: "not_started", label: t("reflectionsAdmin.panel.filter.notStarted") },
            { value: "in_progress", label: t("reflectionsAdmin.panel.filter.inProgress") },
            { value: "submitted", label: t("reflectionsAdmin.panel.filter.submitted") },
            { value: "report_unviewed", label: t("reflectionsAdmin.panel.filter.reportNotViewed") },
        ];
    }
    return [
        { value: "all", label: t("reflectionsAdmin.panel.filter.all") },
        // Pending means "hasn't responded"; once the campaign closes nobody still can.
        { value: "pending", label: campaignState === "closed" ? t("reflectionsAdmin.panel.filter.noResponse") : t("reflectionsAdmin.panel.filter.pending") },
        { value: "responded", label: t("reflectionsAdmin.panel.filter.responded") },
    ];
});

const searchLabel = computed(() => (onParticipants.value ? t("reflectionsAdmin.panel.searchParticipants") : t("reflectionsAdmin.panel.searchRaters")));

const emptyText = computed(() => {
    const filtered = effectiveQuery.value !== "" || filter.value !== "all";
    if (onParticipants.value) {
        return filtered ? t("reflectionsAdmin.panel.noParticipantsMatch") : t("reflectionsAdmin.panel.noParticipantsYet");
    }
    return filtered ? t("reflectionsAdmin.panel.noRatersMatch") : t("reflectionsAdmin.panel.noRatersYet");
});

const footerText = computed(() => {
    if (!page.value) {
        return "";
    }
    const from = page.value.total ? (page.value.page - 1) * page.value.page_size + 1 : 0;
    const to = Math.min(page.value.total, page.value.page * page.value.page_size);
    const counts = { from, to, total: page.value.total };
    return onParticipants.value ? t("reflectionsAdmin.panel.showingParticipants", counts) : t("reflectionsAdmin.panel.showingRaters", counts);
});

let requestSequence = 0;

async function load() {
    const sequence = ++requestSequence;
    loading.value = true;
    loadError.value = null;
    const params = new URLSearchParams({ state: filter.value, q: effectiveQuery.value, page: String(pageNumber.value) });
    const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${campaignId}/${tab.value}?${params}`).json<AdminParticipantsPage | AdminRatersPage>();
    if (sequence !== requestSequence) {
        return;
    }
    loading.value = false;
    if (fetchError.value) {
        const fallback = onParticipants.value ? t("reflectionsAdmin.panel.loadParticipantsFailed") : t("reflectionsAdmin.panel.loadRatersFailed");
        loadError.value = fetchError.value.message || fallback;
        return;
    }
    page.value = data.value ?? null;
}

function showToast(message: ToastMessage) {
    toast.value = { id: ++toastSequence, ...message };
}

function openBulkReminder() {
    reminderTarget.value = onParticipants.value ? { audience: "participants" } : { audience: "raters" };
    reminderOpen.value = true;
}

function openParticipantReminder(row: AdminParticipantRow) {
    reminderTarget.value = { audience: "participants", userUuid: row.user_uuid, name: row.name };
    reminderOpen.value = true;
}

function openRaterReminder(row: AdminRaterRow) {
    reminderTarget.value = { audience: "raters", inviteId: row.invite_id, name: row.rater_name };
    reminderOpen.value = true;
}

async function sendReminder() {
    const target = reminderTarget.value;
    if (!target || sending.value) {
        return;
    }
    sending.value = true;
    const body = target.audience === "participants" ? { user_uuid: target.userUuid ?? null } : { invite_id: target.inviteId ?? null };
    const {
        data,
        error: fetchError,
        statusCode,
    } = await useFetch(`/v1/reflections/admin/campaigns/${campaignId}/${target.audience}/reminders`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
    }).json<AdminReminderResult>();
    sending.value = false;
    reminderOpen.value = false;
    if (fetchError.value) {
        if (statusCode.value === 429) {
            showToast({ theme: "info", text: t("reflectionsAdmin.panel.remind.recentlyReminded", { name: target.name }) });
        } else {
            showToast({ theme: "error", text: (data.value as { error?: string } | null)?.error || t("reflectionsAdmin.panel.remind.failed") });
        }
        return;
    }
    const result = data.value;
    if (!result) {
        showToast({ theme: "error", text: t("reflectionsAdmin.panel.remind.failed") });
        return;
    }
    if (result.sent === 0) {
        // Zero with nothing skipped means nobody was eligible (e.g. only expired
        // invites remain), not that everyone is inside the cooldown.
        showToast({
            theme: "info",
            text: result.skipped > 0 ? t("reflectionsAdmin.panel.remind.nobodySent") : t("reflectionsAdmin.panel.remind.nobodyEligible"),
        });
        return;
    }
    const sentText =
        target.audience === "participants"
            ? t("reflectionsAdmin.panel.remind.sentParticipants", { count: result.sent })
            : t("reflectionsAdmin.panel.remind.sentRaters", { count: result.sent });
    showToast({ theme: "success", text: result.skipped > 0 ? `${sentText} ${t("reflectionsAdmin.panel.remind.skipped", { count: result.skipped })}` : sentText });
}

function switchTab(next: PanelTab) {
    if (tab.value === next) {
        return;
    }
    tab.value = next;
    filter.value = "all";
    query.value = "";
    effectiveQuery.value = "";
    pageNumber.value = 1;
    page.value = null;
}

function selectFilter(next: PanelFilter) {
    filter.value = next;
    pageNumber.value = 1;
}

function goToPage(next: number) {
    pageNumber.value = next;
}

// A new search term always starts from page one.
watch(debouncedQuery, (value) => {
    if (effectiveQuery.value !== value) {
        effectiveQuery.value = value;
        pageNumber.value = 1;
    }
});

watch([tab, filter, effectiveQuery, pageNumber], () => {
    void load();
});

onMounted(() => {
    void load();
});

onBeforeUnmount(() => {
    requestSequence += 1;
});
</script>
